import { Injectable, OnDestroy } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class MobileBackService implements OnDestroy {
  /**
   * Danh sách các hàm callback để đóng modal/overlay.
   * Lưu dưới dạng Object gồm ID (để định danh đúng modal cần đóng) và cb (hàm xử lý đóng).
   */
  private modalStack: Array<{
    id: number,
    cb: () => void,
    /** Keep this surface open when Back only closes a URL-managed child dialog. */
    preserveOnOwnHistoryEntry: boolean,
  }> = [];

  private nextId = 1;

  /**
   * Flag đánh dấu xem thao tác popstate tiếp theo có phải do ta tự gọi history.back() hay không.
   * Nếu có, ta sẽ bỏ qua event popstate đó để tránh đóng nhầm 2 modal liên tiếp.
   */
  private isProgrammaticBack = false;
  private programmaticBackResolver: (() => void) | null = null;

  private boundPopStateListener: (event: PopStateEvent) => void;

  constructor() {
    this.boundPopStateListener = this.onPopState.bind(this);
    window.addEventListener('popstate', this.boundPopStateListener);
  }

  ngOnDestroy() {
    window.removeEventListener('popstate', this.boundPopStateListener);
  }

  /**
   * Gọi hàm này khi MỞ 1 modal/overlay.
   * @param closeCallback Hàm sẽ được thực thi khi user vuốt back hoặc bấm nút back cứng.
   * @returns ID của bản ghi để dùng khi gọi pop().
   */
  push(closeCallback: () => void, options: {preserveOnOwnHistoryEntry?: boolean} = {}): number {
    const id = this.nextId++;
    this.modalStack.push({
      id,
      cb: closeCallback,
      preserveOnOwnHistoryEntry: options.preserveOnOwnHistoryEntry ?? false,
    });

    // Chèn 1 bản ghi ảo vào lịch sử duyệt web với state { isMobileOverlay: true, id }
    // URL vẫn giữ nguyên không đổi.
    history.pushState({ isMobileOverlay: true, id }, '', location.href);
    return id;
  }

  /**
   * Gọi hàm này khi TỰ ĐÓNG 1 modal/overlay bằng tay (VD: user click nút X, hoặc click ra ngoài).
   * Mục đích: Dọn dẹp bản ghi ảo vừa chèn trong history để tránh làm rác lịch sử.
   * @param id Truyền vào ID đã nhận được từ hàm push(). Nếu không truyền sẽ lấy modal trên cùng.
   */
  pop(id?: number) {
    let index = -1;
    if (id !== undefined) {
      index = this.modalStack.findIndex(m => m.id === id);
    } else {
      index = this.modalStack.length > 0 ? this.modalStack.length - 1 : -1;
    }

    if (index !== -1) {
      this.modalStack.splice(index, 1);

      // Đánh dấu lần popstate sắp tới là do code tự gọi, không phải do user vuốt back
      this.isProgrammaticBack = true;
      
      // Lùi lịch sử lại 1 bước để xoá cái state ảo đã chèn lúc push()
      history.back();
    }
  }

  /** Xóa một history entry ảo và chờ browser hoàn tất popstate trước khi mở surface kế tiếp. */
  popAndWait(id?: number): Promise<void> {
    let index = -1;
    if (id !== undefined) {
      index = this.modalStack.findIndex(m => m.id === id);
    } else {
      index = this.modalStack.length > 0 ? this.modalStack.length - 1 : -1;
    }
    if (index === -1) return Promise.resolve();

    this.modalStack.splice(index, 1);
    this.isProgrammaticBack = true;
    return new Promise(resolve => {
      this.programmaticBackResolver = resolve;
      history.back();
    });
  }

  /** Còn overlay/chat đang giữ history entry ảo (Back sẽ đóng lớp đó). */
  hasOpenEntries(): boolean {
    return this.modalStack.length > 0;
  }

  /** Quay lại history entry do router tạo ra mà không đóng overlay/chat đang mở. */
  backWithoutHandling(): void {
    this.isProgrammaticBack = true;
    history.back();
  }

  /**
   * Như backWithoutHandling() nhưng chờ browser xử lý xong popstate.
   * Caller phải chắc chắn có entry phía trước, nếu không popstate sẽ không bao giờ tới.
   */
  backWithoutHandlingAndWait(): Promise<void> {
    this.isProgrammaticBack = true;
    return new Promise(resolve => {
      this.programmaticBackResolver = resolve;
      history.back();
    });
  }

  /**
   * Lắng nghe sự kiện popstate (xảy ra khi lùi/tiến lịch sử duyệt web).
   */
  private onPopState(event: PopStateEvent) {
    // Nếu event này sinh ra do hàm pop() tự gọi history.back(), ta bỏ qua
    if (this.isProgrammaticBack) {
      this.isProgrammaticBack = false;
      const resolver = this.programmaticBackResolver;
      this.programmaticBackResolver = null;
      // Let Angular finish processing this popstate navigation before callers
      // replace the URL (for example, clearing a deep-link cid on Back).
      if (resolver) setTimeout(resolver);
      return;
    }

    // Nếu event này do user tự thao tác (vuốt back / bấm nút back cứng)
    if (this.modalStack.length > 0) {
      const activeOverlay = this.modalStack[this.modalStack.length - 1];
      // Back from a URL-managed dialog lands on its parent sheet entry. Keep
      // that sheet open, but never apply this rule to the chat entry itself:
      // chat Back must return to Inbox on the first press, especially in a
      // native WebView where there may be no previous in-app history entry.
      if (activeOverlay?.preserveOnOwnHistoryEntry
        && event.state?.isMobileOverlay
        && event.state.id === activeOverlay.id) return;
      // Lấy modal trên cùng ra và đóng nó lại
      const topModal = this.modalStack.pop();
      if (topModal && topModal.cb) {
        topModal.cb();
      }
    }
  }
}
